// Homepage rendering

import { setSEO, pageSEO } from '../seo';
import { loadProducts, renderSkeletonProducts } from '../components/products';
import { attachCheckoutFormListeners } from '../components/checkout';
import { resetCheckoutButton } from '../components/order-summary';

export async function renderHomepage(app: HTMLElement) {
    setSEO(pageSEO.home);

    app.innerHTML = `
        <!-- Drawer Structure -->
        <div class="drawer drawer-end">
            <input id="cart-drawer" type="checkbox" class="drawer-toggle" />

            <!-- Main Content -->
            <div class="drawer-content">
                <!-- Glassmorphic Header -->
                <div class="navbar glass fixed top-0 z-50 shadow-lg">
                    <div class="navbar-start">
                        <a href="https://unilorinsu.com" target="_blank" rel="noopener noreferrer"
                           class="btn btn-ghost btn-sm gap-2">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                            <span class="hidden sm:inline">Main Site</span>
                        </a>
                    </div>
                    <div class="navbar-center">
                        <div class="text-center">
                            <div class="font-bold text-lg md:text-xl bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                                Unilorin@50
                            </div>
                            <div class="text-xs opacity-70 hidden sm:block">Golden Jubilee Collection</div>
                        </div>
                    </div>
                    <div class="navbar-end gap-2">
                        <label for="cart-drawer" class="btn btn-ghost btn-circle relative cursor-pointer">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                            </svg>
                            <span id="cart-count" class="badge badge-sm badge-primary absolute -top-1 -right-1">0</span>
                        </label>
                    </div>
                </div>

        <!-- Hero Section -->
        <div class="hero min-h-[70vh] hero-pattern relative overflow-hidden">
            <div class="hero-overlay bg-gradient-to-b from-transparent via-base-100/50 to-base-100"></div>
            <div class="hero-content text-center z-10 py-20">
                <div class="max-w-4xl fade-in">
                    <div class="inline-block mb-4">
                        <div class="badge badge-lg badge-primary gap-2 shadow-lg">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                            </svg>
                            Limited Edition
                        </div>
                    </div>

                    <h1 class="text-5xl md:text-7xl font-bold mb-6 leading-tight">
                        Celebrate <span class="bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent sparkle">50 Years</span><br/>
                        of Excellence
                    </h1>

                    <p class="text-lg md:text-xl mb-8 opacity-80 max-w-2xl mx-auto">
                        Pre-order exclusive Unilorin@50 Golden Jubilee commemorative souvenirs.
                        Limited quantities available.
                    </p>

                    <div class="alert alert-success max-w-md mx-auto shadow-xl">
                        <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span class="font-semibold">Order 3+ items & get a FREE branded packing bag!</span>
                    </div>

                    <div class="mt-8 flex gap-2 justify-center items-center text-sm opacity-60">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        <span>Pickup: Nov 27, 2025 @ SU Building</span>
                    </div>
                </div>
            </div>

            <!-- Scroll Indicator -->
            <div class="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
            </div>
        </div>

        <!-- Main Container -->
        <div class="max-w-7xl mx-auto px-4 py-16 md:py-24">
            <!-- Section Header -->
            <div class="text-center mb-12 slide-up">
                <h2 class="text-4xl md:text-5xl font-bold mb-4">
                    Our Collection
                </h2>
                <p class="text-lg opacity-70 max-w-2xl mx-auto">
                    Exclusive commemorative items celebrating half a century of academic excellence
                </p>
                <div class="divider max-w-xs mx-auto"></div>
            </div>

            <!-- Products Grid -->
            <div id="products-grid" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
                ${renderSkeletonProducts(3)}
            </div>
        </div>

                <!-- Footer -->
                <footer class="footer footer-center p-10 bg-gradient-to-t from-base-200 to-base-100 text-base-content border-t border-base-300">
                    <div class="max-w-4xl mx-auto">
                        <div class="text-center mb-4">
                            <h3 class="font-bold text-2xl bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent mb-2">
                                Unilorin@50
                            </h3>
                            <p class="opacity-70 text-sm">Celebrating Half a Century of Academic Excellence</p>
                        </div>

                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm opacity-70 mb-6">
                            <div>
                                <p class="font-semibold mb-1">Contact</p>
                                <p>Students' Union Building</p>
                                <p>University of Ilorin</p>
                            </div>
                            <div>
                                <p class="font-semibold mb-1">Support</p>
                                <p>For inquiries, visit the SU Building</p>
                            </div>
                        </div>

                        <div class="divider max-w-xs mx-auto"></div>

                        <div>
                            <p class="text-sm opacity-70 mb-2">
                                © ${new Date().getFullYear()} University of Ilorin Students' Union. All rights reserved.
                            </p>
                            <div class="flex items-center justify-center gap-2 text-sm">
                                <span class="opacity-60">Powered by</span>
                                <a href="https://myclass.africa" target="_blank" rel="noopener noreferrer"
                                   class="link link-primary font-semibold hover:text-secondary transition-colors">
                                    myclass.africa
                                </a>
                            </div>
                        </div>
                    </div>
                </footer>
            </div>
            <!-- End of drawer-content -->

            <!-- Cart Drawer Sidebar -->
            <div class="drawer-side z-50">
                <label for="cart-drawer" aria-label="close sidebar" class="drawer-overlay"></label>
                <div class="menu p-0 w-full md:w-[500px] min-h-full bg-base-100 flex flex-col shadow-2xl">
                    <!-- Drawer Header -->
                    <div class="sticky top-0 bg-base-100 border-b border-base-300 p-6 z-10">
                        <div class="flex justify-between items-center">
                            <h3 class="text-2xl font-bold flex items-center gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                                </svg>
                                Your Cart
                            </h3>
                            <label for="cart-drawer" class="btn btn-circle btn-ghost">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </label>
                        </div>
                    </div>

                    <!-- Drawer Content -->
                    <div class="flex-1 overflow-y-auto p-6 custom-scrollbar">
                        <!-- Selected Items -->
                        <div id="selected-items-list" class="mb-6"></div>

                        <!-- Order Summary -->
                        <div class="bg-base-200 rounded-xl p-6 mb-6">
                            <h4 class="font-bold mb-4">Order Summary</h4>
                            <div class="space-y-3">
                                <div class="flex justify-between text-sm">
                                    <span>Subtotal</span>
                                    <strong id="subtotal">₦0</strong>
                                </div>
                                <div class="flex justify-between text-sm text-success">
                                    <span>Commitment Fee (50%)</span>
                                    <strong id="commitment-fee">₦0</strong>
                                </div>
                                <div class="flex justify-between text-xs opacity-70">
                                    <span>Transaction Fee</span>
                                    <strong id="transaction-fee">₦0</strong>
                                </div>
                                <div class="divider my-2"></div>
                                <div class="flex justify-between text-lg font-bold text-primary">
                                    <span>Pay Now</span>
                                    <strong id="amount-to-pay">₦0</strong>
                                </div>
                                <div class="flex justify-between text-sm opacity-70">
                                    <span>Pay on Pickup (50%)</span>
                                    <strong id="balance-due">₦0</strong>
                                </div>
                                <div class="divider my-2"></div>
                                <div class="flex justify-between text-lg font-bold">
                                    <span>Total Value</span>
                                    <strong id="total">₦0</strong>
                                </div>
                            </div>
                        </div>

                        <div id="free-bag-notice" class="alert alert-success shadow-lg mb-6 hidden">
                            <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>You qualify for a FREE branded bag!</span>
                        </div>

                        <!-- Pickup Info -->
                        <div class="alert alert-info mb-6">
                            <div class="text-sm">
                                <div class="font-bold mb-1 flex items-center gap-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    Pickup Details
                                </div>
                                <div>📅 November 27, 2025</div>
                                <div>📍 Students' Union Building, University of Ilorin</div>
                            </div>
                        </div>

                        <!-- Customer Form -->
                        <form id="checkout-form" class="space-y-6">
                            <div class="divider">Contact Information</div>

                            <div class="form-control">
                                <label class="label pb-3">
                                    <span class="label-text font-bold text-base">Full Name *</span>
                                </label>
                                <input
                                    type="text"
                                    id="customer-name"
                                    class="input input-bordered input-lg w-full bg-base-100 border-2 focus:border-primary focus:outline-none px-4"
                                    placeholder="Enter your full name"
                                    required
                                />
                                <label class="label pt-2 hidden" id="name-error-label">
                                    <span class="label-text-alt text-error font-semibold text-xs break-words" id="name-error"></span>
                                </label>
                            </div>

                            <div class="form-control">
                                <label class="label pb-3">
                                    <span class="label-text font-bold text-base">Email Address *</span>
                                </label>
                                <input
                                    type="email"
                                    id="customer-email"
                                    class="input input-bordered input-lg w-full bg-base-100 border-2 focus:border-primary focus:outline-none px-4"
                                    placeholder="your.email@example.com"
                                    required
                                />
                                <label class="label pt-2 hidden" id="email-error-label">
                                    <span class="label-text-alt text-error font-semibold text-xs break-words" id="email-error"></span>
                                </label>
                            </div>

                            <div class="form-control">
                                <label class="label pb-3">
                                    <span class="label-text font-bold text-base">Phone Number *</span>
                                </label>
                                <input
                                    type="tel"
                                    id="customer-phone"
                                    class="input input-bordered input-lg w-full bg-base-100 border-2 focus:border-primary focus:outline-none px-4"
                                    placeholder="08012345678"
                                    required
                                />
                                <label class="label pt-2 hidden" id="phone-error-label">
                                    <span class="label-text-alt text-error font-semibold text-xs break-words" id="phone-error"></span>
                                </label>
                            </div>
                        </form>
                    </div>

                    <!-- Drawer Footer -->
                    <div class="sticky bottom-0 bg-base-100 border-t border-base-300 p-6">
                        <button
                            type="submit"
                            id="checkout-btn"
                            form="checkout-form"
                            class="btn btn-primary btn-lg w-full text-white font-bold !bg-su-blue hover:!bg-su-blue-hover border-0"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                            </svg>
                            Proceed to Payment
                        </button>
                    </div>
                </div>
            </div>
            <!-- End of drawer-side -->
        </div>
        <!-- End of drawer -->

        <!-- Floating Cart Button (FAB) -->
        <label for="cart-drawer" class="btn btn-circle btn-lg btn-primary fixed bottom-6 right-6 shadow-2xl z-40 hover:scale-110 transition-transform cursor-pointer">
            <div class="indicator">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
                <span id="fab-cart-count" class="badge badge-sm badge-secondary indicator-item">0</span>
            </div>
        </label>
    `;

    // Load products
    await loadProducts();

    // Attach event listeners
    attachCheckoutFormListeners();

    // Reset checkout button state (in case user returned from payment)
    resetCheckoutButton();

    // Listen for page visibility changes (when user returns from payment)
    document.addEventListener('visibilitychange', () => {
        if (!document.hidden) {
            resetCheckoutButton();
        }
    });
}
